/**
 * What happens to a policy after it is uploaded: when it comes back for
 * review, who has to read it, and whether it goes straight into approval.
 *
 * WHAT THIS REPLACED. A single fieldset called "Handling", which told a
 * hospital administrator nothing, and which put the acknowledgement AUDIENCE
 * on a different part of the page from the switch deciding whether anybody is
 * asked at all. The audience read as a live choice while acknowledgement was
 * off, which is a question the form could not act on.
 *
 * So the order is now the order of the decisions: does anyone have to read
 * this, and only then, who. Nothing about what the backend stores changed —
 * `targetAudience` is still 'departments' only when a department is chosen
 * AND that radio is selected, which is the rule `audienceFields` applies in
 * functions/src/policies/saveDraft.ts.
 *
 * Shared by the single upload and the batch, and both need the SAME controls:
 * a hospital that learns "every year" is the second button on one page should
 * not find it the third on another. `plural` only changes the wording.
 */

import { SegmentedChoice } from '../../shared/SegmentedChoice';
import type { Audience } from './TaxonomyFields';
import own from './UploadPage.module.css';

/** How often a policy comes back for review. Months, as the backend stores it. */
export const REVIEW_CYCLES = [
  { value: 6, label: 'Every 6 months' },
  { value: 12, label: 'Every year' },
  { value: 24, label: 'Every 2 years' },
  { value: 36, label: 'Every 3 years' },
] as const;

export interface ReviewAndAcknowledgementProps {
  reviewCycle: number;
  onReviewCycle: (months: number) => void;
  requiresAck: boolean;
  onRequiresAck: (on: boolean) => void;
  audience: Audience;
  onAudience: (v: Audience) => void;
  /** Empty when no department is chosen, which closes the department audience. */
  departmentId: string;
  /** Shown in the radio, so the choice names the department rather than implying one. */
  departmentName?: string;
  disabled?: boolean;
  plural?: boolean;
  idPrefix?: string;
}

export function ReviewAndAcknowledgementFields({
  reviewCycle,
  onReviewCycle,
  requiresAck,
  onRequiresAck,
  audience,
  onAudience,
  departmentId,
  departmentName,
  disabled = false,
  plural = false,
  idPrefix = 'policy',
}: ReviewAndAcknowledgementProps) {
  const these = plural ? 'these policies' : 'this policy';
  const id = (s: string) => `${idPrefix}-${s}`;
  const departmentOpen = Boolean(departmentId);

  return (
    <>
      <div className={own.field}>
        {/* Four buttons, one down at a time. A radio group underneath, so
            the platform keeps the "exactly one" rule and a screen reader
            hears "2 of 4" — see shared/SegmentedChoice. */}
        <SegmentedChoice
          legend="Review cycle"
          legendClassName={own.label}
          stacked
          name={`${idPrefix}-review-cycle`}
          options={REVIEW_CYCLES}
          value={reviewCycle}
          onChange={onReviewCycle}
          disabled={disabled}
          describedBy={`${idPrefix}-review-cycle-hint`}
        />
        <p className={own.hint} id={`${idPrefix}-review-cycle-hint`}>
          When {these} will next be due for review, counted from publication.
        </p>
      </div>

      <fieldset className={own.fieldset}>
        <legend className={own.label}>Acknowledgement</legend>

        <label className={own.check}>
          <input
            type="checkbox"
            checked={requiresAck}
            onChange={(e) => onRequiresAck(e.target.checked)}
            disabled={disabled}
          />
          <span>
            Staff must acknowledge {these}
            <span className={own.hint}>
              They will be asked to confirm they have read {plural ? 'each one' : 'it'} once
              published.
            </span>
          </span>
        </label>

        {/*
          The audience appears only once somebody is being asked. Hidden
          rather than disabled: a disabled pair of radios still reads as a
          decision waiting to be made, and there is no decision here until
          the box above is ticked.
        */}
        {requiresAck && (
          <div className={own.subChoice}>
            <fieldset className={own.fieldset} disabled={disabled}>
              <legend className={own.subLabel}>Who must acknowledge {plural ? 'them' : 'it'}?</legend>
              <label className={own.radio}>
                <input
                  type="radio"
                  name={id('audience')}
                  value="all"
                  checked={audience === 'all'}
                  onChange={() => onAudience('all')}
                />
                <span>All staff in the organization</span>
              </label>
              <label className={own.radio}>
                <input
                  type="radio"
                  name={id('audience')}
                  value="departments"
                  checked={audience === 'departments'}
                  onChange={() => onAudience('departments')}
                  disabled={!departmentOpen}
                  aria-describedby={departmentOpen ? undefined : id('audience-hint')}
                />
                <span className={departmentOpen ? undefined : own.choiceOff}>
                  {departmentOpen && departmentName
                    ? `Only ${departmentName}`
                    : 'Only the chosen department'}
                </span>
              </label>
              <p className={own.hint} id={id('audience-hint')}>
                {departmentOpen
                  ? `Every active member of that department is asked when ${plural ? 'each policy is' : 'it is'} published; nobody else.`
                  : 'Choose a department above to limit acknowledgement to it.'}
              </p>
            </fieldset>
          </div>
        )}
      </fieldset>
    </>
  );
}

export interface ApprovalFieldsProps {
  submitForReview: boolean;
  onSubmitForReview: (on: boolean) => void;
  disabled?: boolean;
  plural?: boolean;
}

export function ApprovalFields({
  submitForReview,
  onSubmitForReview,
  disabled = false,
  plural = false,
}: ApprovalFieldsProps) {
  return (
    <label className={own.check}>
      <input
        type="checkbox"
        checked={submitForReview}
        onChange={(e) => onSubmitForReview(e.target.checked)}
        disabled={disabled}
      />
      <span>
        Submit for approval now
        <span className={own.hint}>
          Leave this off to keep {plural ? 'them as drafts' : 'it as a draft'} you can come
          back to.
        </span>
      </span>
    </label>
  );
}
